"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";
import { Mail, Lock, Loader2, ArrowRight } from "lucide-react";
import { useUser } from "@/components/UserContext";
import { API_BASE_URL } from "@/lib/constants";

const LIME = "#ccfc45";

export default function LoginPage() {
    const router = useRouter();
    const { login } = useUser();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [formData, setFormData] = useState({ email: "", password: "" });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            const res = await fetch(`${API_BASE_URL}/api/auth/signin`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ username: formData.email, password: formData.password }),
            });
            const data = await res.json();
            if (res.ok) {
                const token = data.accessToken || data.token;
                const role = data.roles?.includes("ROLE_ADMIN") ? "admin" : "user";
                const name = data.firstName
                    ? `${data.firstName} ${data.lastName || ""}`
                    : (data.username ? data.username.split("@")[0] : "User");
                login(token || "", role, name);
                router.push("/dashboard");
            } else {
                setError(data.message || "Invalid credentials. Please try again.");
            }
        } catch {
            setError("Something went wrong. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-black flex overflow-hidden">
            {/* Left panel — tree/brand visual */}
            <div
                className="hidden lg:flex flex-1 flex-col justify-between p-14 relative overflow-hidden"
                style={{ background: "#0d0d0d" }}
            >
                {/* Glow */}
                <div
                    className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full blur-[150px] opacity-20"
                    style={{ background: LIME }}
                />

                {/* Brand */}
                <Link href="/" className="flex items-center gap-3 z-10">
                    <div className="relative w-10 h-10 rounded-xl overflow-hidden flex items-center justify-center">
                        <Image
                            src="/brand-icon.png"
                            alt="Restaubee Logo"
                            width={40}
                            height={40}
                            className="object-cover"
                        />
                    </div>
                    <span className="font-black text-2xl tracking-tighter text-white">
                        Restau<span style={{ color: LIME }}>bee</span>
                    </span>
                </Link>

                {/* Quotes / features */}
                <div className="z-10">
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3, duration: 0.8 }}
                    >
                        <p className="text-5xl font-black tracking-tighter text-white leading-tight mb-6">
                            Your restaurant.<br />
                            <span style={{ color: LIME }}>Simplified.</span>
                        </p>
                        <p className="text-gray-500 text-lg max-w-sm leading-relaxed">
                            Join thousands of restaurants using Restaubee to manage billing, inventory, and staff — all for free.
                        </p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                        className="flex gap-8 mt-10"
                    >
                        {[["10K+", "Restaurants"], ["$0", "Cost"], ["99.9%", "Uptime"]].map(([n, l]) => (
                            <div key={l}>
                                <div className="text-2xl font-black" style={{ color: LIME }}>{n}</div>
                                <div className="text-gray-600 text-xs font-bold uppercase tracking-widest">{l}</div>
                            </div>
                        ))}
                    </motion.div>
                </div>
            </div>

            {/* Right panel — form */}
            <div className="flex-1 max-w-lg w-full flex items-center justify-center px-8 py-12 mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="w-full max-w-md"
                >
                    {/* Mobile logo */}
                    <div className="lg:hidden flex items-center gap-2 mb-10">
                        <div className="relative w-8 h-8 rounded-lg overflow-hidden flex items-center justify-center">
                            <Image
                                src="/brand-icon.png"
                                alt="Restaubee Logo"
                                width={32}
                                height={32}
                                className="object-cover"
                            />
                        </div>
                        <span className="font-black text-xl tracking-tighter text-white">
                            Restau<span style={{ color: LIME }}>bee</span>
                        </span>
                    </div>

                    <h1 className="text-4xl font-black tracking-tighter text-white mb-2">
                        Welcome back.
                    </h1>
                    <p className="text-gray-500 mb-10 font-medium">
                        Sign in to your POS dashboard.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        {error && (
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm font-bold"
                            >
                                {error}
                            </motion.div>
                        )}

                        {/* Email */}
                        <div className="space-y-2">
                            <label className="text-xs font-black uppercase tracking-wider text-gray-500">
                                Email Address
                            </label>
                            <div className="relative group">
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600 group-focus-within:text-white transition-colors" size={18} />
                                <input
                                    type="email"
                                    name="email"
                                    required
                                    placeholder="you@example.com"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className="w-full h-14 bg-white/5 border border-white/8 rounded-xl pl-12 pr-4 text-white font-bold text-sm focus:outline-none focus:border-white/20 focus:bg-white/8 transition-all placeholder:text-gray-700"
                                />
                            </div>
                        </div>

                        {/* Password */}
                        <div className="space-y-2">
                            <div className="flex justify-between">
                                <label className="text-xs font-black uppercase tracking-wider text-gray-500">
                                    Password
                                </label>
                                <Link href="#" className="text-[10px] font-black uppercase tracking-widest text-gray-600 hover:text-white transition-colors">
                                    Forgot?
                                </Link>
                            </div>
                            <div className="relative group">
                                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600 group-focus-within:text-white transition-colors" size={18} />
                                <input
                                    type="password"
                                    name="password"
                                    required
                                    placeholder="••••••••"
                                    value={formData.password}
                                    onChange={handleChange}
                                    className="w-full h-14 bg-white/5 border border-white/8 rounded-xl pl-12 pr-4 text-white font-bold text-sm focus:outline-none focus:border-white/20 focus:bg-white/8 transition-all placeholder:text-gray-700"
                                />
                            </div>
                        </div>

                        <motion.button
                            type="submit"
                            disabled={loading}
                            whileTap={{ scale: 0.97 }}
                            className="w-full h-14 rounded-xl font-black text-black text-sm flex items-center justify-center gap-3 transition-opacity disabled:opacity-50"
                            style={{ background: LIME }}
                        >
                            {loading
                                ? <Loader2 className="animate-spin" size={20} />
                                : <><span>Sign In</span><ArrowRight size={18} /></>
                            }
                        </motion.button>
                    </form>

                    <p className="text-center mt-8 text-gray-600 text-sm font-medium">
                        No account?{" "}
                        <Link href="/signup" className="text-white font-black hover:opacity-80 transition-opacity">
                            Create one free →
                        </Link>
                    </p>
                </motion.div>
            </div>
        </div>
    );
}
